// SecureEmbed Academic Discovery Portal - Researcher Layout
import { useState } from 'react'
import { Outlet, NavLink, Navigate } from 'react-router-dom'
import {
  Home,
  Search,
  BookOpen,
  Share2,
  List,
  Users,
  PlusCircle,
  BookMarked,
  Settings,
  Shield,
  X,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useRole } from '../../contexts/RoleContext'
import BrandedHeader from './BrandedHeader'

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  adminOnly?: boolean
}

const navItems: NavItem[] = [
  { to: '/', label: 'Home', icon: <Home size={18} /> },
  { to: '/search', label: 'Search', icon: <Search size={18} /> },
  { to: '/browse', label: 'Browse', icon: <BookOpen size={18} /> },
  { to: '/connections', label: 'Connections', icon: <Share2 size={18} /> },
  { to: '/lists', label: 'My Lists', icon: <List size={18} /> },
  { to: '/shared', label: 'Shared', icon: <Users size={18} /> },
  { to: '/add', label: 'Add Resource', icon: <PlusCircle size={18} /> },
  { to: '/mendeley', label: 'Mendeley', icon: <BookMarked size={18} /> },
  { to: '/settings', label: 'Settings', icon: <Settings size={18} /> },
  { to: '/admin', label: 'Admin', icon: <Shield size={18} />, adminOnly: true },
]

function NavLinkItem({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-acad-50 text-acad-700 border-l-2 border-acad-500'
            : 'text-gray-600 hover:bg-gray-50 border-l-2 border-transparent'
        }`
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  )
}

export default function ResearcherLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isAuthenticated, loading } = useAuth()
  const { hasRole } = useRole()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-acad-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const isAdmin = hasRole('uni-admin')

  const visibleNavItems = navItems.filter(
    (item) => !item.adminOnly || isAdmin
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandedHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />

      <div className="flex">
        {/* Sidebar - desktop */}
        <aside className="hidden md:flex md:flex-col md:w-56 md:min-h-[calc(100vh-64px)] bg-white border-r border-gray-200">
          <nav className="flex-1 py-4 space-y-0.5">
            {visibleNavItems.map((item) => (
              <NavLinkItem key={item.to} item={item} />
            ))}
          </nav>
        </aside>

        {/* Sidebar - mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div
              className="fixed inset-0 bg-black/30"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed inset-y-0 left-0 z-50 w-56 bg-white border-r border-gray-200 shadow-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-700">Navigation</span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 rounded"
                >
                  <X size={18} />
                </button>
              </div>
              <nav className="py-4 space-y-0.5">
                {visibleNavItems.map((item) => (
                  <div key={item.to} onClick={() => setSidebarOpen(false)}>
                    <NavLinkItem item={item} />
                  </div>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 max-w-7xl mx-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
